import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Check, GripVertical, Plus, Trash2 } from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";
import { Modal } from "@/components/Modal";
import {
  DAY_KEYS,
  DAY_LABELS,
  type DayKey,
  isDayKey,
  previousIsoWeek,
} from "@/lib/days";
import { findWeekByYearAndNumber } from "@/lib/repos/weeks";
import type {
  PlannedWorkout,
  WorkoutLogConfig,
  WorkoutLogWeekData,
} from "@/lib/schema";
import {
  acceptsPlans,
  canEditPlan,
  copyPlan,
  dayIndex,
  plannedOn,
  plannedWorkouts,
  saveDayFor,
} from "./plan";

interface Props {
  config: WorkoutLogConfig;
  weekData: unknown;
  kept: Set<string>;
  todayIndex: number;
  year: number;
  weekNumber: number;
  onSave: (next: WorkoutLogWeekData) => void;
  onClose: () => void;
}

/**
 * Last week's plan, when last week was a Planlagt uke for this same instance
 * — the only source «Kopier forrige uke» copies from. Null otherwise.
 */
function usePreviousPlan(
  config: WorkoutLogConfig,
  year: number,
  weekNumber: number,
): PlannedWorkout[] | null {
  const [previous, setPrevious] = useState<PlannedWorkout[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    const prior = previousIsoWeek(year, weekNumber);
    findWeekByYearAndNumber(prior.year, prior.weekNumber).then((week) => {
      if (cancelled || !week) return;
      const entry = week.data.widgets.find((w) => w.config.id === config.id);
      if (entry?.config.kind !== "workout-log") return;
      const priorConfig = entry.config as WorkoutLogConfig;
      if (plannedOn(priorConfig, entry.week) === null) return;
      setPrevious(plannedWorkouts(priorConfig, entry.week));
    });
    return () => {
      cancelled = true;
    };
  }, [config.id, year, weekNumber]);
  return previous;
}

/**
 * The week's plan, edited as a draft and written in one go by «Lagre plan».
 * All plan editing lives here; the card only ticks (CONTEXT.md: Planlagt økt).
 */
export function PlanWeekModal({
  config,
  weekData,
  kept,
  todayIndex,
  year,
  weekNumber,
  onSave,
  onClose,
}: Props) {
  const [draft, setDraft] = useState<PlannedWorkout[]>(() =>
    plannedWorkouts(config, weekData),
  );
  const previous = usePreviousPlan(config, year, weekNumber);
  const savedOn = plannedOn(config, weekData);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    if (!e.over) return;
    const target = String(e.over.id);
    if (!isDayKey(target) || !acceptsPlans(target, todayIndex)) return;
    const id = String(e.active.id);
    if (kept.has(id)) return;
    setDraft((d) => {
      const plan = d.find((p) => p.id === id);
      if (!plan || plan.day === target) return d;
      // Appended, so the moved plan lands last on its new day.
      return [...d.filter((p) => p.id !== id), { ...plan, day: target }];
    });
  };

  const add = (day: DayKey, label: string) => {
    setDraft((d) => [...d, { id: crypto.randomUUID(), label, day }]);
  };

  const rename = (id: string, label: string) => {
    setDraft((d) => d.map((p) => (p.id === id ? { ...p, label } : p)));
  };

  const remove = (id: string) => {
    setDraft((d) => d.filter((p) => p.id !== id));
  };

  const save = () => {
    const plans = draft
      .map((p) => ({ ...p, label: p.label.trim() }))
      .filter((p) => p.label.length > 0 || kept.has(p.id));
    onSave({
      // A later save never moves the marker (docs/adr/0026).
      plannedOn: savedOn ?? saveDayFor(todayIndex),
      plannedWorkouts: plans,
    });
  };

  const canCopy =
    draft.length === 0 && previous !== null && previous.length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      data-testid={`workout-plan-modal-${config.id}`}
    >
      <h2>Planlegg uka</h2>
      <p className="hint">
        Dager uten økt er hviledager. Dra en økt for å flytte den.
      </p>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="workout-plan-days">
          {DAY_KEYS.map((day) => (
            <PlanDay
              key={day}
              day={day}
              plans={draft.filter((p) => p.day === day)}
              kept={kept}
              todayIndex={todayIndex}
              onAdd={add}
              onRename={rename}
              onRemove={remove}
            />
          ))}
        </div>
      </DndContext>
      <div className="modal-actions">
        {canCopy ? (
          <button
            type="button"
            className="btn ghost"
            onClick={() => setDraft(copyPlan(previous ?? [], todayIndex))}
            data-testid="workout-plan-copy-button"
          >
            Kopier forrige uke
          </button>
        ) : null}
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="workout-plan-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          data-testid="workout-plan-save-button"
        >
          Lagre plan
        </button>
      </div>
    </Modal>
  );
}

interface PlanDayProps {
  day: DayKey;
  plans: PlannedWorkout[];
  kept: Set<string>;
  todayIndex: number;
  onAdd: (day: DayKey, label: string) => void;
  onRename: (id: string, label: string) => void;
  onRemove: (id: string) => void;
}

function PlanDay({
  day,
  plans,
  kept,
  todayIndex,
  onAdd,
  onRename,
  onRemove,
}: PlanDayProps) {
  const open = acceptsPlans(day, todayIndex);
  const { setNodeRef, isOver } = useDroppable({ id: day, disabled: !open });
  const [value, setValue] = useState("");

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onAdd(day, trimmed);
    setValue("");
  };

  const past = dayIndex(day) < todayIndex;
  return (
    <section
      ref={setNodeRef}
      className={`workout-plan-day${past ? " past" : ""}${isOver && open ? " over" : ""}`}
      data-testid={`workout-plan-day-${day}`}
    >
      <h3>
        {DAY_LABELS[day]}
        {dayIndex(day) === todayIndex ? (
          <span className="hint"> · i dag</span>
        ) : null}
      </h3>
      {plans.length === 0 ? (
        <p className="hint" data-testid={`workout-plan-rest-${day}`}>
          Hvile
        </p>
      ) : (
        plans.map((plan) => (
          <PlanRow
            key={plan.id}
            plan={plan}
            kept={kept.has(plan.id)}
            editable={canEditPlan(plan, kept, todayIndex)}
            onRename={onRename}
            onRemove={onRemove}
          />
        ))
      )}
      {open ? (
        <div className="cl-add">
          <input
            type="text"
            placeholder="Legg til økt"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            data-testid={`workout-plan-input-${day}`}
          />
          <button
            type="button"
            onClick={submit}
            aria-label="Legg til"
            data-testid={`workout-plan-add-button-${day}`}
          >
            <Plus size={14} />
          </button>
        </div>
      ) : null}
    </section>
  );
}

interface PlanRowProps {
  plan: PlannedWorkout;
  kept: boolean;
  editable: boolean;
  onRename: (id: string, label: string) => void;
  onRemove: (id: string) => void;
}

/**
 * A kept plan is locked (docs/adr/0025); a missed one can only be dragged
 * forward; anything on today or later is free.
 */
function PlanRow({ plan, kept, editable, onRename, onRemove }: PlanRowProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: plan.id, disabled: kept });
  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cl-item${kept ? " done" : ""}${isDragging ? " dragging" : ""}`}
      data-testid={`workout-plan-row-${plan.id}`}
      data-kept={kept ? "true" : "false"}
    >
      {kept ? (
        <span className="check on" role="img" aria-label="Gjort">
          <Check size={14} />
        </span>
      ) : (
        <button
          type="button"
          className="cl-drag icon-btn-drag"
          aria-label="Flytt"
          data-testid={`workout-plan-drag-handle-${plan.id}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={14} />
        </button>
      )}
      {editable ? (
        <input
          type="text"
          className="workout-plan-label-input"
          value={plan.label}
          onChange={(e) => onRename(plan.id, e.target.value)}
          data-testid={`workout-plan-label-${plan.id}`}
        />
      ) : (
        <span className="cl-text" data-testid={`workout-plan-label-${plan.id}`}>
          {plan.label}
        </span>
      )}
      {editable ? (
        <button
          type="button"
          className="cl-del icon-btn-delete"
          onClick={() => onRemove(plan.id)}
          aria-label="Slett"
          data-testid={`workout-plan-delete-${plan.id}`}
        >
          <Trash2 size={14} />
        </button>
      ) : null}
    </div>
  );
}
